import React, {useMemo} from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CheckRounded, CloseRounded } from "@mui/icons-material";
import { SupplierLocationDisplayVO } from "../../../types/LocationStatus";
import { LocationReviewCompletion } from "../../../util/LocationCompletionUtil";
import { LocationReasonDeactivated } from "../../../types/model/status/LocationReasonDeactivated";

interface LocationCardSearchChipProps {
  location: SupplierLocationDisplayVO;
  locationReviewCompletion: LocationReviewCompletion;
}

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "ownerVisible",
})<{ ownerVisible: boolean }>(({ ownerVisible }) => ({
  height: 22,
  fontSize: "0.7857rem",  // 11px at 14px root
  fontWeight: 700,
  borderRadius: 3,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  "& .MuiChip-label": { padding: "0 8px 0 4px" },
  "& .MuiChip-icon": { color: "inherit", fontSize: "0.8571rem", marginLeft: 6, marginRight: 0 },
  ...(ownerVisible
    ? { background: "#e6f9ee", color: "#2e9e59", border: "1px solid #a8e6be" }
    : { background: "#fde8e8", color: "#c04040", border: "1px solid #f5b8b8" }),
}));

const SubLabel = styled(Box)({
  fontSize: "0.7857rem",  // 11px at 14px root
  color: "#5E7782",
  marginTop: 2,
});

const DeactivatedReason = styled("span")({
  fontWeight: 700,
  color: "#c04040",
  cursor: "help",
});


/**
 * "Visible in search" / "Not visible in search" chip indicating whether the
 * location appears in buyer search results, with an optional reason below.
 */

const UNKNOWN_REASON_DEACTIVATED = "Unknown Reason";
const UNKNOWN_REASON_DEACTIVATED_NOTE = "No additional context.";

const LocationCardSearchChip = ({ 
  location,
  locationReviewCompletion
}: LocationCardSearchChipProps) => {


  const sublabel = useMemo(() => {
    let subLabelText: React.ReactNode;
    switch (location.derivedStatus) {
      case "pending":
        subLabelText = "Pending Approval";
        break;
      case "draft":
        subLabelText = locationReviewCompletion.isComplete ? "All required fields complete" : "Missing Required Fields";
        break;
      case "deactivated": {
        const reasonDeactivated = LocationReasonDeactivated[location.reasonDeactivated as keyof typeof LocationReasonDeactivated] ?? UNKNOWN_REASON_DEACTIVATED;
        const reasonDeactivatedNote = location.reasonDeactivatedNote || UNKNOWN_REASON_DEACTIVATED_NOTE;
        subLabelText = (
          <>
            {`Deactivated on ${location.lastModifiedDate ?? "—"} `}
            <Tooltip title={reasonDeactivatedNote} arrow>
              <DeactivatedReason tabIndex={0}>{reasonDeactivated}</DeactivatedReason>
            </Tooltip>
          </>
        );
        break;
      }
      default:
        subLabelText = undefined;
    }

    return subLabelText ? <SubLabel>{subLabelText}</SubLabel> : null;
  }, [location, locationReviewCompletion]);

  return <Box>
    <StatusChip
      ownerVisible={location.isVisible}
      size="small"
      icon={location.isVisible ? <CheckRounded aria-hidden="true" /> : <CloseRounded aria-hidden="true" />}
      label={location.isVisible ? "Visible in search" : "Not visible in search"}
    />
    {sublabel}
  </Box>
}

export default LocationCardSearchChip;

